import Shepherd from 'shepherd.js';
import 'shepherd.js/dist/css/shepherd.css';

const PALETTE = {
  inchworm: '#B1FA63',
  gunmetal: '#243837',
  orange: '#FE7733',
  paleViolet: '#B2A1FF',
  brightSnow: '#FFFFFF',
  americanSilver: '#D1D1D1',
};

// Tour completion tracking
const TOUR_COMPLETED_KEY = 'brandcomply_tour_completed';
const INTRO_SHOWN_KEY = 'brandcomply_intro_shown';

export const hasCompletedTour = (): boolean => {
  return localStorage.getItem(TOUR_COMPLETED_KEY) === 'true';
};

export const markTourCompleted = (): void => {
  localStorage.setItem(TOUR_COMPLETED_KEY, 'true');
};

// Check if intro tour has been shown (for first-time users)
export const hasSeenIntroTour = (): boolean => {
  return localStorage.getItem(INTRO_SHOWN_KEY) === 'true';
};

// Mark intro tour as shown - call this BEFORE starting the tour
export const markIntroTourShown = (): void => {
  localStorage.setItem(INTRO_SHOWN_KEY, 'true');
};

export const resetTourStatus = (): void => {
  localStorage.removeItem(TOUR_COMPLETED_KEY);
  localStorage.removeItem(INTRO_SHOWN_KEY);
};

// Common tour configuration
export const getTourConfig = () => ({
  defaultStepOptions: {
    cancelIcon: { enabled: true },
    scrollTo: false, // Disable auto-scroll to prevent forcing elements to top
    classes: 'shadow-lg bg-white shepherd-custom-buttons',
  },
  useModalOverlay: true,
});

// CSS styles for tour buttons - inject once
export const tourStyles = `
  /* Shepherd tour custom button styles */
  .shepherd-custom-buttons .shepherd-button {
    background-color: ${PALETTE.inchworm} !important;
    color: ${PALETTE.gunmetal} !important;
    border: 1px solid ${PALETTE.inchworm} !important;
    border-radius: 6px !important;
    padding: 8px 16px !important;
    font-weight: 500 !important;
    transition: all 0.2s ease !important;
  }
  
  .shepherd-custom-buttons .shepherd-button:hover {
    background-color: #A3E635 !important;
    border-color: #A3E635 !important;
    transform: translateY(-1px) !important;
  }
  
  .shepherd-custom-buttons .shepherd-button:focus {
    outline: none !important;
    box-shadow: 0 0 0 2px rgba(177, 250, 99, 0.3) !important;
  }

  .shepherd-custom-buttons .shepherd-button-secondary {
    background-color: ${PALETTE.brightSnow} !important;
    color: ${PALETTE.gunmetal} !important;
    border: 1px solid ${PALETTE.americanSilver} !important;
  }

  .shepherd-custom-buttons .shepherd-button-secondary:hover {
    background-color: #f5f5f5 !important;
    border-color: ${PALETTE.americanSilver} !important;
  }

  .shepherd-content {
    border-radius: 12px !important;
    padding: 0 !important;
  }

  .shepherd-text {
    padding: 16px 20px !important;
    color: ${PALETTE.gunmetal} !important;
    font-size: 14px !important;
    line-height: 1.6 !important;
  }

  .shepherd-header {
    padding: 16px 20px 8px !important;
    border-bottom: none !important;
  }

  .shepherd-title {
    color: ${PALETTE.gunmetal} !important;
    font-weight: 600 !important;
    font-size: 16px !important;
  }

  .shepherd-footer {
    padding: 12px 20px 16px !important;
    border-top: none !important;
  }

  .shepherd-cancel-icon {
    color: ${PALETTE.americanSilver} !important;
  }

  .shepherd-cancel-icon:hover {
    color: ${PALETTE.gunmetal} !important;
  }

`;

// Helper to create standard buttons
export const createBackNextButtons = (tour: Shepherd.Tour) => [
  {
    text: 'Back',
    classes: 'shepherd-button-secondary',
    action: function() { tour.back(); }
  },
  {
    text: 'Next',
    action: function() { tour.next(); }
  }
];

export const createNextOnlyButton = (tour: Shepherd.Tour) => [
  {
    text: 'Next',
    action: function() { tour.next(); }
  }
];

export const createBackDoneButtons = (tour: Shepherd.Tour) => [
  {
    text: 'Back',
    classes: 'shepherd-button-secondary',
    action: function() { tour.back(); }
  },
  {
    text: 'Done',
    action: function() { tour.complete(); }
  }
];

export const createDoneOnlyButton = (tour: Shepherd.Tour) => [
  {
    text: 'Got it!',
    action: function() { tour.complete(); }
  }
];

const waitForElement = (selector: string, timeoutMs = 6000) => {
  return new Promise<void>((resolve, reject) => {
    const start = Date.now();
    const check = () => {
      if (document.querySelector(selector)) {
        resolve();
        return;
      }
      if (Date.now() - start > timeoutMs) {
        reject(new Error(`Element not found: ${selector}`));
        return;
      }
      requestAnimationFrame(check);
    };
    check();
  });
};

// ============================================
// BRAND USER TOUR - Context-Aware Tours
// ============================================

// Detect current step based on visible elements
export const detectBrandStep = (): 'upload' | 'review' | 'published' | 'campaign-selected' | 'none' => {
  if (document.querySelector('.submission-link-copy-btn')) return 'published';
  if (document.querySelector('.campaign-name-input')) return 'review';
  if (document.querySelector('.briefing-upload-dropzone')) return 'upload';
  if (document.querySelector('.copy-submission-link-btn')) return 'campaign-selected';
  return 'none';
};

// Tour for initial welcome + sidebar overview
export const createBrandWelcomeTour = (
  setSidebarOpen?: (open: boolean) => void,
  isAutoStart: boolean = false
): Shepherd.Tour => {
  const tour = new Shepherd.Tour(getTourConfig());

  // Mark tour as completed when it ends
  tour.on('complete', () => {
    markTourCompleted();
  });

  // Also mark as completed if user cancels (closes via X button)
  tour.on('cancel', () => {
    markTourCompleted();
  });

  // Add intro step only for auto-started tours (new users)
  if (isAutoStart) {
    tour.addStep({
      id: 'brand-intro',
      title: 'Welcome to BrandComply!',
      text: `
        <div style="text-align: center; padding: 10px 0;">
          <p style="font-size: 15px; margin-bottom: 12px;">
            We're excited to have you here!
          </p>
          <p style="font-size: 14px; color: #666; margin-bottom: 16px;">
            This quick tour will walk you through everything you need to know about managing creator campaigns and reviewing video submissions.
          </p>
          <p style="font-size: 13px; color: #888;">
            It only takes about 2 minutes. Let's get started!
          </p>
        </div>
      `,
      scrollTo: false,
      canClickTarget: false,
      buttons: [
        {
          text: 'Skip Tour',
          classes: 'shepherd-button-secondary',
          action: function() { 
            markTourCompleted();
            tour.complete(); 
          }
        },
        {
          text: "Let's Go! →",
          action: function() { tour.next(); }
        }
      ]
    });
  }

  tour.addStep({
    id: 'brand-sidebar',
    title: 'Your Campaigns',
    text: 'Your campaigns appear in the sidebar. Click any campaign to view its submissions.',
    attachTo: { element: '.brand-sidebar', on: 'right' },
    beforeShowPromise: () => {
      return new Promise<void>((resolve) => {
        if (setSidebarOpen) setSidebarOpen(true);
        setTimeout(resolve, 300);
      });
    },
    buttons: createBackNextButtons(tour)
  });

  tour.addStep({
    id: 'brand-profile',
    title: 'Your Profile',
    text: 'Click your profile to access account settings, see usage, manage your subscription, adjust notifications and log out.',
    attachTo: { element: '.sidebar-profile-btn', on: 'right' },
    buttons: createBackNextButtons(tour)
  });

  tour.addStep({
    id: 'brand-new-campaign',
    title: 'Create a New Campaign',
    text: 'Click "New Campaign" to start creating your first campaign. Let\'s try it now!',
    attachTo: { element: '.new-campaign-btn', on: 'right' },
    buttons: createBackDoneButtons(tour)
  });

  return tour;
};

// Tour for Upload step (briefing upload)
export const createBrandUploadTour = (): Shepherd.Tour => {
  const tour = new Shepherd.Tour(getTourConfig());

  tour.addStep({
    id: 'brand-upload-intro',
    title: 'Upload Your Briefing',
    text: 'Here you can upload your campaign briefing document or paste the briefing text.',
    scrollTo: false,
    canClickTarget: false,
    buttons: createNextOnlyButton(tour)
  });

  tour.addStep({
    id: 'brand-briefing-upload',
    title: 'Upload or Paste Requirements',
    text: 'Drag and drop your briefing file here. You can also switch to "Paste Text" tab if you prefer to copy-paste.',
    attachTo: { element: '.briefing-upload-dropzone', on: 'right' },
    buttons: createBackNextButtons(tour)
  });

  tour.addStep({
    id: 'brand-briefing-recommendation',
    title: 'Upload Your File Now!',
    text: 'For the best results, upload a PDF, Word, or TXT file with your campaign briefing. Go ahead and upload your file now!',
    attachTo: { element: '.briefing-upload-dropzone', on: 'right' },
    buttons: [
      {
        text: 'Got it!',
        action: function() { tour.complete(); }
      }
    ]
  });

  return tour;
};

// Tour for when file is selected (shows Extract button)
export const createBrandExtractTour = (): Shepherd.Tour => {
  const tour = new Shepherd.Tour(getTourConfig());

  tour.addStep({
    id: 'brand-file-ready',
    title: 'File Ready! ✅',
    text: 'Great! Your briefing file is uploaded and ready to be processed.',
    attachTo: { element: '.briefing-upload-dropzone', on: 'right' },
    buttons: createNextOnlyButton(tour)
  });

  tour.addStep({
    id: 'brand-extract-requirements',
    title: 'Extract Requirements',
    text: 'Click this button to extract the requirements from your briefing. This may take a few seconds.',
    attachTo: { element: '.extract-requirements-btn', on: 'top' },
    buttons: [
      {
        text: 'Got it!',
        action: function() { tour.complete(); }
      }
    ]
  });

  return tour;
};

// Tour for Review step
export const createBrandReviewTour = (): Shepherd.Tour => {
  const tour = new Shepherd.Tour(getTourConfig());

  tour.addStep({
    id: 'brand-review-intro',
    title: 'Review & Edit Your Campaign Briefing',
    text: 'BrandComply has extracted the requirements from your briefing. Review and edit them before publishing a submission link.',
    scrollTo: false,
    canClickTarget: false,
    buttons: createNextOnlyButton(tour)
  });

  tour.addStep({
    id: 'brand-review-campaign-name',
    title: 'Campaign Name',
    text: 'This is your campaign name. Make sure it\'s clear and recognizable for your creators.',
    attachTo: { element: '.campaign-name-input', on: 'bottom' },
    buttons: createBackNextButtons(tour)
  });

  tour.addStep({
    id: 'brand-logo-url',
    title: 'Brand Logo URL',
    text: 'Add a brand logo URL so we know what logo must appear in the videos.',
    attachTo: { element: '.brand-logo-url-input', on: 'bottom' },
    buttons: createBackNextButtons(tour)
  });

  tour.addStep({
    id: 'brand-product-url',
    title: 'Product Image URL',
    text: 'Add a product image URL so we know which product must be featured in the videos.',
    attachTo: { element: '.product-image-url-input', on: 'bottom' },
    buttons: createBackNextButtons(tour)
  });

  tour.addStep({
    id: 'brand-requirements-section',
    title: 'Requirements Categories',
    text: 'Requirements are organized by category: Hooks, Topics, Call to Actions, Don\'ts, Story, and Visuals.',
    attachTo: { element: '.requirements-section', on: 'top' },
    buttons: createBackNextButtons(tour)
  });

  tour.addStep({
    id: 'brand-add-requirement',
    title: 'Add Requirements',
    text: 'Use the "Add" button to create new requirements in any category.',
    attachTo: { element: '.add-requirement-btn', on: 'left' },
    buttons: createBackNextButtons(tour)
  });

  tour.addStep({
    id: 'brand-delete-requirement',
    title: 'Delete Requirements',
    text: 'Remove any requirement by clicking the X button next to it.',
    attachTo: { element: '.delete-requirement-btn', on: 'left' },
    buttons: createBackNextButtons(tour)
  });

  tour.addStep({
    id: 'brand-publish-briefing',
    title: 'Generate Submission Link',
    text: 'When everything looks good, click here to publish and generate a submission link for your creators to submit their videos!',
    attachTo: { element: '.publish-briefing-btn', on: 'top' },
    beforeShowPromise: function() {
      return waitForElement('.publish-briefing-btn').then(() => {
        const target = document.querySelector('.publish-briefing-btn');
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
      });
    },
    buttons: [
      {
        text: 'Back',
        classes: 'shepherd-button-secondary',
        action: function() { tour.back(); }
      },
      {
        text: 'Got it!',
        action: function() { tour.complete(); }
      }
    ]
  });

  return tour;
};

// Tour for Published step (submission link)
export const createBrandPublishedTour = (): Shepherd.Tour => {
  const tour = new Shepherd.Tour(getTourConfig());

  tour.addStep({
    id: 'brand-published-intro',
    title: 'Campaign Published!',
    text: 'Your campaign briefing is now live and ready for creators to submit their videos.',
    scrollTo: false,
    canClickTarget: false,
    buttons: createNextOnlyButton(tour)
  });

  tour.addStep({
    id: 'brand-submission-link',
    title: 'Copy the Submission Link',
    text: 'Click this button to copy the link. Share it with your creators via email, messaging, or your project management tool. Each submission will be automatically checked against your campaign briefing.',
    attachTo: { element: '.submission-link-copy-btn', on: 'top' },
    buttons: [
      {
        text: 'Back',
        classes: 'shepherd-button-secondary',
        action: function() { tour.back(); }
      },
      {
        text: 'Done!',
        action: function() { tour.complete(); }
      }
    ]
  });

  return tour;
};

// Tour for when a campaign is selected (viewing submissions)
export const createBrandCampaignSelectedTour = (): Shepherd.Tour => {
  const tour = new Shepherd.Tour(getTourConfig());

  tour.addStep({
    id: 'brand-campaign-overview',
    title: 'Campaign Overview',
    text: 'Here you can see all submissions for this campaign and manage creator approvals and revisions.',
    scrollTo: false,
    canClickTarget: false,
    buttons: createNextOnlyButton(tour)
  });

  tour.addStep({
    id: 'brand-copy-link',
    title: 'Share with More Creators',
    text: 'Copy this link to share with additional creators who need to submit videos.',
    attachTo: { element: '.copy-submission-link-btn', on: 'bottom' },
    buttons: createBackNextButtons(tour)
  });

  tour.addStep({
    id: 'brand-view-briefing',
    title: 'View Campaign Briefing',
    text: 'Click here to review all the requirements for this campaign.',
    attachTo: { element: '.view-briefing-btn', on: 'bottom' },
    buttons: createBackNextButtons(tour)
  });

  tour.addStep({
    id: 'brand-submissions-table',
    title: 'Creator Submissions',
    text: 'All submissions appear here with creator name, status, timestamp, version, and BrandComply score. Click "View" in the "Detailed Results" column for detailed results.',
    attachTo: { element: '.ag-theme-quartz', on: 'top' },
    buttons: createBackNextButtons(tour)
  });

  tour.addStep({
    id: 'brand-approval',
    title: 'Approve or Request Revisions',
    text: 'For submitted videos, use the Approve or Revision buttons. This marks the submission as approved or requests revisions, and generates an email template you can customize and send to the creator.',
    attachTo: { element: '.approval-column-header', on: 'bottom' },
    buttons: createBackNextButtons(tour)
  });

  tour.addStep({
    id: 'brand-sidebar-tips',
    title: 'Sidebar Quick Tips',
    text: 'Use the sidebar to switch between campaigns, create new ones, and access your profile settings and logout. It\'s the fastest way to navigate your campaign dashboards.',
    attachTo: { element: '.brand-sidebar', on: 'right' },
    buttons: createBackDoneButtons(tour)
  });

  return tour;
};

// Main tour function - detects context and starts appropriate tour
export const createBrandCampaignTour = (
  setSidebarOpen?: (open: boolean) => void,
  currentStep?: 'upload' | 'review' | 'published' | null,
  selectedCampaignUuid?: string | null,
  hasFile?: boolean,
  isAutoStart: boolean = false
): Shepherd.Tour => {
  // If a campaign is selected (viewing submissions)
  if (selectedCampaignUuid && !currentStep) {
    return createBrandCampaignSelectedTour();
  }

  // Detect context based on current step
  if (currentStep === 'published') {
    return createBrandPublishedTour();
  }

  if (currentStep === 'review') {
    return createBrandReviewTour();
  }

  if (currentStep === 'upload') {
    // Check if file is already selected (extract button visible)
    if (hasFile || document.querySelector('.extract-requirements-btn')) {
      return createBrandExtractTour();
    }
    return createBrandUploadTour();
  }

  // Default: show welcome tour (with intro step for auto-start)
  return createBrandWelcomeTour(setSidebarOpen, isAutoStart);
};

// ============================================
// CREATOR TOUR - Campaign Loader (Step 1)
// ============================================
export const createCreatorLoaderTour = (): Shepherd.Tour => {
  const tour = new Shepherd.Tour(getTourConfig());

  // Step 1: Welcome
  tour.addStep({
    id: 'creator-welcome',
    title: 'Welcome to BrandComply!',
    text: 'This tool helps you check your video against the campaign briefing before submitting it Let\'s get started!',
    scrollTo: false,
    canClickTarget: false,
    buttons: createNextOnlyButton(tour)
  });

  // Step 2: Campaign Info
  tour.addStep({
    id: 'creator-campaign-info',
    title: 'Campaign Details',
    text: 'This shows the campaign you\'re submitting to. Make sure it matches what you expect!',
    attachTo: { element: '.campaign-info-section', on: 'bottom' },
    buttons: createBackNextButtons(tour)
  });

  // Step 3: Name Fields
  tour.addStep({
    id: 'creator-name-fields',
    title: 'Your Information',
    text: 'Enter your first name and surname. This helps us identify your submission.',
    attachTo: { element: '.name-fields', on: 'bottom' },
    buttons: createBackNextButtons(tour)
  });

  // Step 4: Email Field
  tour.addStep({
    id: 'creator-email',
    title: 'Email Address',
    text: 'Enter your email address. We will use this to contact you about your submission.',
    attachTo: { element: '.email-field', on: 'bottom' },
    buttons: createBackNextButtons(tour)
  });

  // Step 5: Fill in Information
  tour.addStep({
    id: 'creator-fill-info',
    title: 'Ready to Begin?',
    text: 'Go ahead and fill in your information now. Once you\'re done, the "Start Submission" button will appear and we\'ll continue!',
    scrollTo: false,
    canClickTarget: false,
    buttons: [
      {
        text: 'Ok',
        action: function() { tour.complete(); }
      }
    ]
  });

  // Step 6: Start Submission
  tour.addStep({
    id: 'creator-start',
    title: 'Start Your Submission',
    text: 'Perfect! Now click this button to proceed to video upload.',
    attachTo: { element: '.start-submission-btn', on: 'top' },
    buttons: createBackDoneButtons(tour),
    beforeShowPromise: function() {
      return waitForElement('.start-submission-btn');
    }
  });

  return tour;
};

// ============================================
// CREATOR TOUR - Video Upload (Step 2)
// ============================================
export const createCreatorUploadTour = (): Shepherd.Tour => {
  const tour = new Shepherd.Tour(getTourConfig());

  // Step 1: Welcome
  tour.addStep({
    id: 'upload-welcome',
    title: 'Upload Your Video Now',
    text: 'Now it\'s time to upload your video. We will analyze it against the campaign briefing.',
    scrollTo: false,
    canClickTarget: false,
    buttons: createNextOnlyButton(tour)
  });

  // Step 2: Video Drop Zone
  tour.addStep({
    id: 'upload-dropzone',
    title: 'Video Upload Area',
    text: 'Drag and drop your video file here, or click to browse. Supported formats: MP4, MOV, AVI.',
    attachTo: { element: '.video-dropzone', on: 'right' },
    buttons: createBackNextButtons(tour)
  });

  // Step 3: Upload Encouragement
  tour.addStep({
    id: 'upload-encourage',
    title: 'Upload Your Video Now',
    text: 'Go ahead and upload your video now. Once it\'s uploaded, the "Start Analysis" button will appear below!',
    scrollTo: false,
    canClickTarget: false,
    buttons: [
      {
        text: 'Ok',
        action: function() { tour.complete(); }
      }
    ]
  });

  // Step 4: Continue Button
  tour.addStep({
    id: 'upload-continue',
    title: 'Start Analysis',
    text: 'Perfect! Now click this button to start the video check. This usually takes 1-2 minutes.',
    attachTo: { element: '.campaign-details-button', on: 'top' },
    buttons: createBackDoneButtons(tour),
    beforeShowPromise: function() {
      return waitForElement('.campaign-details-button');
    }
  });

  return tour;
};

// ============================================
// CREATOR TOUR - Results (Pending Submission)
// ============================================
export const createCreatorResultsTour = (): Shepherd.Tour => {
  const tour = new Shepherd.Tour(getTourConfig());

  // Step 1: Welcome
  tour.addStep({
    id: 'results-welcome',
    title: 'Your BrandComply Results',
    text: 'Here are your video\'s BrandComply results. Review them before submitting it.',
    scrollTo: false,
    canClickTarget: false,
    buttons: createNextOnlyButton(tour)
  });

  // Step 2: Compliance Score
  tour.addStep({
    id: 'results-score',
    title: 'BrandComply Score',
    text: 'This percentage shows how well your video matches the campaign briefing. For example, 50% means you met 5 of 10 requirements. Higher is better as it usually leads to faster approval.',
    attachTo: { element: '.compliance-score-circle', on: 'left' },
    buttons: createBackNextButtons(tour)
  });

  // Step 3: Video Player
  tour.addStep({
    id: 'results-video',
    title: 'Your Video',
    text: 'Play your video to review it. The overlay shows campaign details.',
    attachTo: { element: '.video-player-container', on: 'right' },
    buttons: createBackNextButtons(tour)
  });

  // Step 4: Compliance Icons
  tour.addStep({
    id: 'results-requirements',
    title: 'Alignment Status Icons',
    text: 'Each requirement has an icon: ✅ for aligned, ❌ for not aligned to the campaign brief. Hover over any icon to see the explanation of why the video is aligned or not.',
    attachTo: { element: '.compliance-icon-container', on: 'right' },
    buttons: createBackNextButtons(tour)
  });

  // Step 5: Comments
  tour.addStep({
    id: 'results-comments',
    title: 'Add Comments',
    text: 'We missed something? You can add comments to explain any items that are not aligned to the campaign brief and submit anyway. These will be seen when reviewing your submission.',
    attachTo: { element: '.comment-column-input, .comment-column-cell', on: 'left' },
    buttons: createBackNextButtons(tour)
  });

  // Step 6: Re-Upload Button
  tour.addStep({
    id: 'results-reupload',
    title: 'Re-Upload Option',
    text: 'Not happy with the results? Click here to upload a new version of your video.',
    attachTo: { element: '.reupload-btn', on: 'top' },
    buttons: createBackNextButtons(tour)
  });

  // Step 7: Submit Button
  tour.addStep({
    id: 'results-submit',
    title: 'Submit Your Video & Success Scan',
    text: 'When you\'re ready, click this button to submit your video and success scan.',
    attachTo: { element: '.submit-btn', on: 'top' },
    buttons: createBackDoneButtons(tour)
  });

  return tour;
};

// ============================================
// BRAND USER TOUR - Viewing Submitted Results
// ============================================
export const createBrandResultsTour = (): Shepherd.Tour => {
  const tour = new Shepherd.Tour(getTourConfig());

  // Step 1: Welcome
  tour.addStep({
    id: 'brand-results-welcome',
    title: 'Submission Details',
    text: 'This page shows the detailed BrandComply results for a creator\'s submission. Review them before approving or requesting revisions.',
    scrollTo: false,
    canClickTarget: false,
    buttons: createNextOnlyButton(tour)
  });

  // Step 2: Compliance Score
  tour.addStep({
    id: 'brand-results-score',
    title: 'BrandComply Score',
    text: 'The BrandComply score shows what percentage of the campaign brief the video aligns with.',
    attachTo: { element: '.compliance-score-circle', on: 'left' },
    buttons: createBackNextButtons(tour)
  });  
  
  // Step 3: Video & Info  
  tour.addStep({
    id: 'brand-results-video',
    title: 'Video & Submission Info',
    text: 'The video player shows the creator\'s submission along with campaign name, version, and submission date.',
    attachTo: { element: '.video-player-container', on: 'right' },
    buttons: createBackNextButtons(tour)
  });

  // Step 4: Compliance Icons
  tour.addStep({
    id: 'brand-results-icons',
    title: 'Alignment Status Icons',
    text: 'Each requirement has an icon: ✅ for aligned, ❌ for not aligned to the campaign brief. Hover over any icon to see the explanation of why the video is aligned or not.',
    attachTo: { element: '.compliance-icon-container', on: 'right' },
    buttons: createBackNextButtons(tour)
  });

  // Step 5: Creator Comments
  tour.addStep({
    id: 'brand-results-comments',
    title: 'Creator Comments',
    text: 'If the creator added any comments explaining their choices, they\'ll appear in this column.',
    attachTo: { element: '.comment-column-header', on: 'left' },
    buttons: createBackNextButtons(tour)
  });

  // Step 6: Download Video
  tour.addStep({
    id: 'brand-results-download',
    title: 'Download Video',
    text: 'Click the three dots (⋮) in the bottom-right corner of the video player to download the video.',
    attachTo: { element: '.video-player-container', on: 'right' },
    buttons: createBackDoneButtons(tour)
  });

  return tour;
};

export default {
  getTourConfig,
  tourStyles,
  createBrandCampaignTour,
  createBrandWelcomeTour,
  createBrandUploadTour,
  createBrandExtractTour,
  createBrandReviewTour,
  createBrandPublishedTour,
  createBrandCampaignSelectedTour,
  createCreatorLoaderTour,
  createCreatorUploadTour,
  createCreatorResultsTour,
  createBrandResultsTour,
  detectBrandStep,
};

